import React, { useEffect, useRef, useState } from "react";
import CardEmpty from "Utils/Cards/CardEmpty";
import LoadMask from "Utils/LoadMask/LoadMask";
import Paginacion from "Utils/Components/Paginacion/Paginacion";
import { useDebounce } from "Utils/Hooks/useDebounce";
import { useForm } from "react-hook-form";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { ReajustesTabApi } from "./api/ReajustesTabApi";
import { FiltrosReajustes } from "./components/FiltrosReajustes";
import { ReajustesInventarioTable } from "../../../../../components/ReajustesInventarioTable";
import { getErrorFromResponse } from "../../../../../../../../../utility/utils";

export const ReajustesTab = ({ storageKey }) => {
    const STORAGE_KEY = storageKey;
    const savedState = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    const filtros = savedState && savedState.filtros ? savedState.filtros : {};

    const { control, watch } = useForm({
        defaultValues: {
            producto: filtros.producto || null,
            pallet: filtros.pallet || "",
            fecha_inicial: filtros.fecha_inicial
                ? moment(filtros.fecha_inicial)
                : null,
            fecha_final: filtros.fecha_final
                ? moment(filtros.fecha_final)
                : null,
        },
    });

    const [reajustes, setReajustes] = useState([]);
    const [paginaSeleccionada, setPaginaSeleccionada] = useState(
        savedState.pagina || 1,
    );
    const [cantidadResultados, setCantidadResultados] = useState(0);

    const producto = watch("producto");
    const pallet = watch("pallet");
    const fecha_inicial = watch("fecha_inicial");
    const fecha_final = watch("fecha_final");

    // el pallet se escribe letra por letra, así que se espera a que el
    // encargado termine antes de consultar
    const palletBuscado = useDebounce(pallet);

    const { getReajustes, getProductos, cargando } = ReajustesTabApi();

    // evita que una consulta lenta de un filtro anterior pise el resultado
    // del que el usuario tiene seleccionado
    const consultaRef = useRef(0);

    const GetReajustes = async () => {
        const consulta = consultaRef.current + 1;
        consultaRef.current = consulta;

        try {
            const params = {
                page: paginaSeleccionada,
            };

            if (producto) {
                params.producto_id = producto.id;
            }

            if (palletBuscado) {
                params.pallet = palletBuscado;
            }

            // el corte lleva hora, pero el filtro es por día completo
            if (fecha_inicial) {
                params.fecha_inicial =
                    moment(fecha_inicial).format("YYYY-MM-DD");
            }

            if (fecha_final) {
                params.fecha_final = moment(fecha_final).format("YYYY-MM-DD");
            }

            const response = await getReajustes(params);

            if (consulta !== consultaRef.current) return;

            setReajustes(response.results);
            setCantidadResultados(response.count);
        } catch (error) {
            if (consulta !== consultaRef.current) return;

            // Si el error es por una página inválida, volvemos a la página anterior
            if (
                error &&
                error.detail === "Invalid page." &&
                paginaSeleccionada > 1
            ) {
                setPaginaSeleccionada((prev) => prev - 1);
                return;
            }

            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener los reajustes de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            setReajustes([]);
            setCantidadResultados(0);
        }
    };

    useEffect(() => {
        GetReajustes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        paginaSeleccionada,
        producto,
        palletBuscado,
        fecha_inicial,
        fecha_final,
    ]);

    useEffect(() => {
        const stateToSave = {
            filtros: {
                producto,
                pallet,
                fecha_inicial,
                fecha_final,
            },
            pagina: paginaSeleccionada,
        };

        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    }, [producto, pallet, fecha_inicial, fecha_final, paginaSeleccionada]);

    return (
        <div className="inventario-bodega__tab-wrapper">
            <LoadMask loading={cargando} blur>
                <CardEmpty noShadow>
                    <div className="mb-3">
                        <FiltrosReajustes
                            control={control}
                            getProductos={getProductos}
                            onRefresh={() => {
                                GetReajustes();
                            }}
                            resetPage={() => setPaginaSeleccionada(1)}
                        />
                    </div>

                    <small className="d-block mb-3 help-text">
                        Los reajustes se registran dentro de un cierre de
                        inventario y corrigen la existencia hasta que el cierre
                        se realiza, por eso aquí solo aparecen los de cierres ya
                        realizados.
                    </small>

                    <ReajustesInventarioTable reajustes={reajustes} />
                    <Paginacion
                        cantidadResultados={cantidadResultados}
                        paginaSeleccionada={paginaSeleccionada}
                        setPaginaSeleccionada={setPaginaSeleccionada}
                    />
                </CardEmpty>
            </LoadMask>
        </div>
    );
};
